import { yupResolver } from "@hookform/resolvers/yup";
import { FieldValues, useForm } from "react-hook-form";
import { Alert, Dialog, DialogActions, DialogContent, Grid } from "@mui/material";
import AppTextInput from "../../../../Component/Common/Components/FormValidationInputs/AppTextInput";
import { LoadingButton } from "@mui/lab";
import { MDButton } from "../../../../Component/Common/Components/MDButton";
import { AppDatepicker } from "../../../../Component/Common/Components/FormValidationInputs/AppDatepicker";
import { useEffect } from "react";
import AppSelectList, { IDropDownOptions } from "../../../../Component/Common/Components/FormValidationInputs/AppSelectList";
import { AllOccupationalLevelList, EmployeeLevelList, provinceList } from "../../../../Component/Common/Constants/Lists/DropdownListOptions";
import React from "react";
import { toast } from "react-toastify";
import { CoreStaticDataService } from "../../../../Component/Api/StaticData/CoreStaticDataService";
import { getIOptionsArray } from "../../../../Helpers/arrayHelper";
import { PlatformPeopleService } from "../../../../Component/Api/PlatformPeople/PlatformPeopleService";
import { PlatformPersonEmploymentDataValidationScheme } from "./PlatformPersonEmploymentDataValidationScheme";
import { PlatformPeopleEmploymentData } from "../../../../Component/Model/Dto/Platform/PlatformPeople";
import { CustomDialogTitle } from "../../../Common/Custom/CustomDialogTitle";
import { useRecoilValue } from "recoil";
import { userRolesState } from "../../../../State/user.Atom";
import { PlatformPeopleEditRole, UserAdminRole } from "../../../../Component/Common/Constants/UserRoles";


interface IProps {
  platformPeopleId: string,
  open: boolean;
  closeModal: () => void;
  platformPeopleEmploymentData?: PlatformPeopleEmploymentData;
}

export const PlatformPersonEmploymentDataModal = ({ open, closeModal, platformPeopleId, platformPeopleEmploymentData }: IProps) => {
  const userRoles = useRecoilValue(userRolesState);
  const [municipalityOptions, setMunicipalityOptions] = React.useState<IDropDownOptions[]>([]);
  const [showErrorAndMessage, setShowErrorAndMessage] = React.useState({
    message: null,
    isError: false
  })
  const editRoleAccess = [
    PlatformPeopleEditRole,
    UserAdminRole
  ]

  const isEditable = userRoles.some(r => editRoleAccess.includes(r));


  const { control, watch, handleSubmit, formState: { isSubmitting }, reset } = useForm({
    mode: 'all',
    resolver: yupResolver(PlatformPersonEmploymentDataValidationScheme),
  });

  const fetchStaticData = async () => {
    try {
      const result = await CoreStaticDataService.getMunicipalities();
      if (result.data) {
        setMunicipalityOptions(getIOptionsArray(result.data, "id", "name"));
      } else {
        toast.warn("No Municipalities found")
      }
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    fetchStaticData();
  }, [])

  useEffect(() => {
    reset(platformPeopleEmploymentData);
  }, [platformPeopleEmploymentData?.platformPeopleEmploymentDataId])

  const handleDataSubmit = async (data: FieldValues) => {
    try {
      setShowErrorAndMessage({
        message: null,
        isError: false
      })
      const finalData = data as PlatformPeopleEmploymentData;

      if (platformPeopleEmploymentData?.platformPeopleEmploymentDataId) {
        finalData.platformPeopleId = platformPeopleId;
        finalData.platformPeopleEmploymentDataId = platformPeopleEmploymentData.platformPeopleEmploymentDataId;
        const result = await PlatformPeopleService.updatePlatformPersonEmploymentData(finalData);
        toast.success(result.data);
      }
      else {
        const finalData = data as PlatformPeopleEmploymentData;
        finalData.platformPeopleId = platformPeopleId;
        const result = await PlatformPeopleService.createPlatformPersonEmploymentData(finalData);
        toast.success(result.data);
      }
      closeEmploymentDataModal();
    } catch (error: any) {
      setShowErrorAndMessage({
        message: error.response.data,
        isError: true
      })
    }
  }
  const closeEmploymentDataModal = async () => {
    reset();
    setShowErrorAndMessage({
      message: null,
      isError: false
    })
    closeModal();
  }
  const titleMessage = isEditable ? "Edit" : "View";
  return (
    <Dialog open={open} onClose={() => closeEmploymentDataModal()} maxWidth={false} aria-labelledby="form-dialog-title">
      <CustomDialogTitle onClose={() => closeEmploymentDataModal()}>
        
        {platformPeopleEmploymentData?.platformPeopleEmploymentDataId ? (titleMessage) : ("Add")} Employment Data
      </CustomDialogTitle>
      <DialogContent sx={{ paddingTop: "5px !important", width: "600px " }}>
        <form id='EmploymentDataForm' onSubmit={handleSubmit(handleDataSubmit)}>
          <Grid container spacing={1}>
            {(showErrorAndMessage.isError && showErrorAndMessage.message) &&
              <Grid item xs={12} sm={12} paddingBottom={2}>
                <Alert severity="error" sx={{ p: 1 }}>{showErrorAndMessage.message}</Alert>
              </Grid>}
            <Grid item xs={12} sm={12}>
              <AppDatepicker label='Start Date *' name='startDate' control={control} inputFormat="yyyy/MM/dd" disabled={!isEditable}/>
            </Grid>
            <Grid item xs={12} sm={12}>
              <AppDatepicker label='End Date' name='endDate' control={control} inputFormat="yyyy/MM/dd" showDescription descriptionText="Required only if the employee has since been terminated" disabled={!isEditable}/>
            </Grid>
            <Grid item xs={12} sm={12}>
              <AppTextInput label='Job Title *' name='jobTitle' control={control} disabled={!isEditable}/>
            </Grid>
            <Grid item xs={12} sm={12}>
              <AppSelectList label='Occupational Level *' name='occupationalLevelId' control={control} items={AllOccupationalLevelList} disabled={!isEditable}/>
            </Grid>
            <Grid item xs={12} sm={12}>
              <AppSelectList label='Employment Type *' name='employmentTypeId' control={control} items={EmployeeLevelList} disabled={!isEditable}/>
            </Grid>
            <Grid item xs={12} sm={12}>
              <AppSelectList label="Province *" name="provinceId" control={control} items={provinceList} defaultValue={null} showDescription descriptionText="This is the province for which the employee is currently positioned." disabled={!isEditable}/>
            </Grid>
            <Grid item xs={12} sm={12}>
              <AppSelectList label='Municipality' name='municipalityId' control={control} items={municipalityOptions} showDescription descriptionText="This is the municipality for which the employee is currently positioned. This could be a requirement for your WSP/ATR Submission." disabled={!isEditable}/>
            </Grid>
            <Grid item xs={12} sm={12}>
              <AppTextInput label='Site Name' name='siteName' control={control} showDescription descriptionText="This is the location for which the employee is currently positioned. " disabled={!isEditable}/>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        {isEditable &&
          <>
            <LoadingButton loading={isSubmitting} variant='contained' form='EmploymentDataForm' type="submit" color='primary'>save</LoadingButton>
            <MDButton variant='outlined' color='primary' onClick={() => closeEmploymentDataModal()}>Cancel</MDButton>
          </>
        }
      </DialogActions>
    </Dialog>
  )
}

